import moment from "moment-timezone";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  return String(timeStr).slice(0, 5);
};

/** True if string is a valid IANA zone for moment-timezone. */
export function isValidIanaTimezone(tz) {
  const s = String(tz || "").trim();
  if (!s) return false;
  return !!moment.tz.zone(s);
}

const TIMEZONE_ALIASES = {
  IST: "Asia/Kolkata",
  "INDIA STANDARD TIME": "Asia/Kolkata",
  GMT: "Etc/GMT",
  UTC: "UTC",
};

/** Map legacy/short labels (e.g. IST) to IANA ids used by moment-timezone. */
export function normalizePreferredContactTimezone(tz) {
  const s = String(tz || "").trim();
  if (!s) return "";
  if (isValidIanaTimezone(s)) return s;
  const mapped = TIMEZONE_ALIASES[s.toUpperCase()];
  if (mapped && isValidIanaTimezone(mapped)) return mapped;
  return s;
}

/**
 * Infer IANA timezone from lead API payload (nested country / city).
 * Uses city.timezone when present; otherwise city name + country code; else country default.
 * @param {object} lead - Lead row or create response (may include country, city)
 * @returns {string} IANA id or "" if unknown
 */
export function inferPreferredTimezoneFromLead(lead) {
  if (!lead || typeof lead !== "object") return "";

  const cityObj = lead.city || lead.general_city || null;
  const countryObj = lead.country || null;

  const fromDb = cityObj?.timezone || cityObj?.time_zone || cityObj?.tz;
  if (fromDb && String(fromDb).trim() && isValidIanaTimezone(fromDb)) {
    return String(fromDb).trim();
  }

  const countryCode = String(
    countryObj?.country_code ??
      countryObj?.countryCode ??
      lead.country_code ??
      ""
  )
    .trim()
    .toUpperCase();

  const cityName = String(
    cityObj?.city_name ?? cityObj?.name ?? lead.city_name ?? ""
  )
    .trim()
    .toLowerCase();

  const norm = (s) => s.replace(/\s+/g, " ").trim();

  // --- Canada: city matters (MT vs ET vs PT) ---
  if (countryCode === "CA" && cityName) {
    const c = norm(cityName);
    if (
      /calgary|edmonton|red deer|lethbridge|medicine hat|fort mcmurray|banff|canmore/.test(
        c
      )
    ) {
      return "America/Edmonton";
    }
    if (/vancouver|victoria|kelowna|surrey|burnaby|richmond/.test(c)) {
      return "America/Vancouver";
    }
    if (/winnipeg|brandon|regina|saskatoon/.test(c)) {
      return "America/Winnipeg";
    }
    if (/halifax|moncton|fredericton|charlottetown|st\.?\s*john'?s/.test(c)) {
      return "America/Halifax";
    }
    // Ontario / Quebec / most eastern population centers
    if (
      /toronto|ottawa|montreal|quebec|mississauga|brampton|hamilton|london|windsor|kitchener|waterloo|guelph|markham|vaughan|oakville|burlington|niagara/.test(
        c
      )
    ) {
      return "America/Toronto";
    }
    return "America/Toronto";
  }

  // --- United States ---
  if (countryCode === "US" && cityName) {
    const c = norm(cityName);
    if (
      /los angeles|san francisco|san diego|seattle|portland|phoenix|las vegas/.test(
        c
      )
    ) {
      return "America/Los_Angeles";
    }
    if (/chicago|dallas|houston|austin|minneapolis|kansas city|st\.?\s*louis/.test(c)) {
      return "America/Chicago";
    }
    if (/denver|salt lake|boise/.test(c)) {
      return "America/Denver";
    }
    return "America/New_York";
  }

  // --- Country defaults (single-timezone or common business default) ---
  const byCountry = {
    IN: "Asia/Kolkata",
    NP: "Asia/Kathmandu",
    AE: "Asia/Dubai",
    SA: "Asia/Riyadh",
    GB: "Europe/London",
    IE: "Europe/Dublin",
    DE: "Europe/Berlin",
    FR: "Europe/Paris",
    IT: "Europe/Rome",
    ES: "Europe/Madrid",
    NL: "Europe/Amsterdam",
    BE: "Europe/Brussels",
    PL: "Europe/Warsaw",
    AU: "Australia/Sydney",
    NZ: "Pacific/Auckland",
    JP: "Asia/Tokyo",
    KR: "Asia/Seoul",
    CN: "Asia/Shanghai",
    SG: "Asia/Singapore",
    MY: "Asia/Kuala_Lumpur",
    PH: "Asia/Manila",
    TH: "Asia/Bangkok",
    PK: "Asia/Karachi",
    BD: "Asia/Dhaka",
    LK: "Asia/Colombo",
    ZA: "Africa/Johannesburg",
    EG: "Africa/Cairo",
    NG: "Africa/Lagos",
    BR: "America/Sao_Paulo",
    MX: "America/Mexico_City",
  };

  if (countryCode && byCountry[countryCode]) {
    return byCountry[countryCode];
  }

  return "";
}

/**
 * Convert a clock time in `sourceTz` on `dateYmd` to IST and return HH:mm:ss.
 * @param {string} dateYmd - YYYY-MM-DD (DST anchor)
 * @param {string} timeHms - HH:mm or HH:mm:ss in source timezone
 * @param {string} sourceTz - IANA e.g. America/Toronto
 */
export function localTimeToIstHms(dateYmd, timeHms, sourceTz) {
  let t = String(timeHms || "").trim();
  if (!t) return "";
  if (t.length === 5) t += ":00";
  const m = moment.tz(`${dateYmd} ${t}`, "YYYY-MM-DD HH:mm:ss", sourceTz);
  if (!m.isValid()) return "";
  return m.tz("Asia/Kolkata").format("HH:mm:ss");
}

/**
 * Lead contact timing: fixed local windows (7–9 AM, 7–9 PM) in lead's timezone → IST fields.
 * Mutates `data` with student times as HH:mm:ss and all four IST times as HH:mm:ss.
 * @param {object} data - processed form payload (must include preferred_contact_timezone)
 * @param {string} [referenceDateYmd] - defaults to today (for DST)
 */
export function applyLeadContactTimingIstFields(data, referenceDateYmd) {
  const tz = normalizePreferredContactTimezone(data.preferred_contact_timezone);
  data.preferred_contact_timezone = tz;
  const dateYmd =
    referenceDateYmd || moment().format("YYYY-MM-DD");

  const pickStudentHm = (value, fallback) => {
    const hm = formatTime(value);
    return hm || fallback;
  };

  const studentTimes = {
    morning_student_time_start: pickStudentHm(
      data.morning_student_time_start,
      "07:00"
    ),
    morning_student_time_end: pickStudentHm(
      data.morning_student_time_end,
      "09:00"
    ),
    evening_student_time_start: pickStudentHm(
      data.evening_student_time_start,
      "19:00"
    ),
    evening_student_time_end: pickStudentHm(
      data.evening_student_time_end,
      "21:00"
    ),
  };

  Object.assign(data, studentTimes);

  const toHms = (hm) => (hm && hm.length === 5 ? `${hm}:00` : hm || "");

  data.morning_ist_time_start = localTimeToIstHms(
    dateYmd,
    toHms(studentTimes.morning_student_time_start),
    tz
  );
  data.morning_ist_time_end = localTimeToIstHms(
    dateYmd,
    toHms(studentTimes.morning_student_time_end),
    tz
  );
  data.evening_ist_time_start = localTimeToIstHms(
    dateYmd,
    toHms(studentTimes.evening_student_time_start),
    tz
  );
  data.evening_ist_time_end = localTimeToIstHms(
    dateYmd,
    toHms(studentTimes.evening_student_time_end),
    tz
  );

  return data;
}

/** @deprecated Prefer localTimeToIstHms — kept for any old imports */
function convertToIST(inputTime, sourceTZ) {
  const d = moment().format("YYYY-MM-DD");
  return localTimeToIstHms(d, inputTime, sourceTZ);
}

export { formatDate, formatTime, convertToIST };
